import { Button, TextField, type DialogProps } from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { number, object, string } from 'yup';
import { useCreateDeviceGroup } from '~/api/device-groups';
import {
  Dialog,
  DialogContentTitle,
  DialogForm,
  DialogTitle,
} from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib';
import { useAppContext } from '~/contexts';
import { usePaginationParams } from '~/hooks/pagination';
import { i18n } from '~/lib/i18n';

const validationSchema = object({
  description: string().optional().label(i18n.t('common.labels.description')),
  name: string().required(i18n.t('errors.validation.required')).label(i18n.t('common.labels.name')),
  networkId: number().required(),
});

export const NewDeviceGroupDialog = (props: DialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [createGroup] = useCreateDeviceGroup();

  const [searchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() || '';
  const { page, perPage } = usePaginationParams();

  // Sucks
  const close = () => props.onClose?.({}, 'backdropClick');

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      description: '',
      name: '',
      networkId: currentNetwork.id,
    },
    onSubmit: async (input) => {
      const { errors } = await createGroup({
        variables: { input: { ...input, networkId: currentNetwork.id }, page, perPage, search },
      });
      if (!errors?.length) close();
    },
    validateOnBlur: true,
    validationSchema,
  });

  return (
    <Dialog {...props}>
      <DialogTitle onClose={close}>{t('pages.device_groups.new_dialog.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>{t('pages.device_groups.new_dialog.description')}</DialogContentTitle>

        <DialogForm id="new-device-group" onSubmit={formik.handleSubmit}>
          <label>
            <span className={`label required`}>{t('common.labels.name')}</span>
            <TextField
              autoFocus
              error={formik.touched.name && !!formik.errors.name}
              fullWidth
              helperText={(formik.touched.name && formik.errors.name) || ' '}
              name="name"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.name}
            />
          </label>

          <label>
            <span className={`label`}>{t('common.labels.description')}</span>
            <TextField
              error={formik.touched.description && !!formik.errors.description}
              fullWidth
              helperText={(formik.touched.description && formik.errors.description) || ' '}
              minRows={4}
              maxRows={4}
              multiline
              name="description"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.description}
            />
          </label>
        </DialogForm>
      </DialogContent>

      <DialogActions>
        <Button disabled={formik.isSubmitting} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>

        <Button
          color="primary"
          disabled={formik.isSubmitting || !formik.isValid || !formik.dirty}
          form="new-device-group"
          type="submit"
          variant="contained"
        >
          {t('common.dialogs.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
